import { Permission, PermissionValue } from "./permissions";
import { UserRole, UserRoleValue } from "./roles";

// Admin always has every permission in the catalog — new permissions are
// automatically granted to Admin without needing to be listed here twice.
const ALL_PERMISSIONS: PermissionValue[] = Object.values(Permission);

// "My ..." pages about a student's / faculty's own records, not general admin capabilities, so
// they are excluded from Admin's otherwise-automatic "every permission" grant. (Admin already sees
// all payments via Finance, every faculty's week via Timetable → By Faculty, and all leave via
// Staff → Leave Requests.)
const ADMIN_EXCLUDED: PermissionValue[] = [
  Permission.FEES_PAYMENTS_VIEW_OWN,
  Permission.TIMETABLE_VIEW_OWN,
  Permission.LEAVE_APPLY_OWN,
  Permission.SUBSTITUTIONS_VIEW_OWN,
];

export const RolePermissions: Record<UserRoleValue, PermissionValue[]> = {
  [UserRole.ADMIN]: ALL_PERMISSIONS.filter((permission) => !ADMIN_EXCLUDED.includes(permission)),

  [UserRole.FACULTY]: [
    Permission.STUDENTS_ATTENDANCE_MARK, // marks attendance for their own class
    Permission.FEES_VIEW, // Faculty should be able to only view their own students dues
    Permission.TIMETABLE_VIEW_OWN, // My Space → My Timetable (read-only)
    Permission.LEAVE_APPLY_OWN, // My Space → My Leave
    Permission.SUBSTITUTIONS_VIEW_OWN, // My Space → My Substitutions
  ],

  [UserRole.STUDENT]: [
    Permission.FEES_VIEW, // own dues
    Permission.FEES_RECEIPTS_VIEW, // own receipts
    Permission.FEES_PAYMENTS_VIEW_OWN, // own online payment history
  ],

  // No menu access defined yet — unused role today, revisit if it gets one.
  [UserRole.OPERATOR]: [],

  // UserRole.COORDINATOR is added here in Phase 3, once the backend issues
  // the role. Per the agreed scope it's ALL_PERMISSIONS minus:
  //   CONFIG_ACADEMICS_MANAGE, CONFIG_FEE_STRUCTURE_MANAGE,
  //   USERS_MANAGE, CONFIG_SYSTEM_MANAGE
};

export const hasPermission = (role: UserRoleValue | undefined, permission: PermissionValue): boolean => {
  if (!role) return false;
  return RolePermissions[role]?.includes(permission) ?? false;
};
